import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { Buildings, Plus, MagnifyingGlass, ToggleLeft, ToggleRight, ArrowLeft, PencilSimple } from '@phosphor-icons/react';
import { BADGE, timeAgo, RISK_STYLES, STATUS_STYLES, formatLabel } from '@/lib/format';
import { displayRelation } from '@/lib/relations';

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ name: '', address: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { document.title = 'Radar — Accounts'; }, []);

  useEffect(() => {
    api.get('/organizations', { params: { limit: 500 } })
      .then(r => setOrgs(r.data.organizations || []))
      .catch(() => setOrgs([]));
  }, []);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (orgFilter) params.org_id = orgFilter;
      const r = await api.get('/accounts', { params });
      setAccounts(r.data.accounts || []);
    } catch {}
    setLoading(false);
  }, [search, statusFilter, orgFilter]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const toggleStatus = async (id) => {
    try { await api.post(`/accounts/${id}/toggle-status`); fetchAccounts(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post('/accounts', form);
      setShowCreate(false);
      setForm({ name: '', address: '', status: 'active' });
      fetchAccounts();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create'); }
    setSaving(false);
  };

  return (
    <div data-testid="accounts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">Accounts</h1>
          <p className="text-sm text-[#52525B]">Manage corporate and individual accounts that own cases</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-account-btn" className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] transition-all">
          <Plus size={16} /> New Account
        </button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or account ID…" data-testid="accounts-search" className="w-full pl-9 pr-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#0052FF]" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="accounts-status-filter" className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)} data-testid="accounts-org-filter" className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md min-w-[180px]">
          <option value="">Search by Org — All</option>
          {orgs.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>
      ) : accounts.length === 0 ? (
        <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
          <Buildings size={48} className="mx-auto text-[#A1A1AA] mb-3" />
          <p className="text-sm text-[#52525B] mb-1">No accounts yet</p>
          <p className="text-xs text-[#A1A1AA]">Create your first account to start linking cases</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Account ID</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Name</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Address</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Cases</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Created</th>
                <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Action</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id} data-testid={`account-row-${a.id}`} className="border-b border-[#F0F0F1] hover:bg-[#F7F7F8] transition-colors">
                  <td className="px-4 py-3 text-xs font-mono text-[#52525B]">{a.account_id}</td>
                  <td className="px-4 py-3 text-sm font-medium text-[#0052FF] cursor-pointer hover:underline" onClick={() => navigate(`/accounts/${a.id}`)}>{a.name}</td>
                  <td className="px-4 py-3 text-xs text-[#52525B] max-w-[260px] truncate">{a.address || '—'}</td>
                  <td className="px-4 py-3"><span className={`${BADGE} ${a.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{a.status === 'active' ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-4 py-3 text-sm">{a.cases_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-[#A1A1AA]">{a.created_at ? timeAgo(a.created_at) : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => toggleStatus(a.id)} data-testid={`toggle-${a.id}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${a.status === 'active' ? 'border-[#E4E4E7] text-[#52525B] hover:bg-[#F0F0F1]' : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'}`}>
                      {a.status === 'active' ? <><ToggleRight size={14} weight="fill" /> Deactivate</> : <><ToggleLeft size={14} /> Make Active</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} onClick={e => e.stopPropagation()} className="bg-white rounded-md border border-[#E4E4E7] shadow-lg w-full max-w-md p-6" data-testid="new-account-modal">
            <h2 className="font-heading text-lg font-semibold mb-4">New Account</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Account Name *</label>
                <input autoFocus required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="account-name-input" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Address</label>
                <textarea value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} rows={2} data-testid="account-address-input" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} data-testid="account-status-input" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <p className="text-[11px] text-[#A1A1AA]">Account ID will be generated automatically (e.g., ACC-000001).</p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Cancel</button>
              <button type="submit" disabled={saving || !form.name.trim()} data-testid="save-account-btn" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">{saving ? 'Creating…' : 'Create Account'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Account Detail Page
// ──────────────────────────────────────────────────────────────
export function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', status: 'active' });

  const fetchAccount = useCallback(async () => {
    try {
      const r = await api.get(`/accounts/${id}`);
      setAccount(r.data);
      setForm({ name: r.data.name || '', address: r.data.address || '', status: r.data.status || 'active' });
      document.title = `Radar — ${r.data.name}`;
    } catch {}
  }, [id]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  const toggleStatus = async () => {
    try { await api.post(`/accounts/${id}/toggle-status`); fetchAccount(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const save = async () => {
    try { await api.put(`/accounts/${id}`, form); setEditing(false); fetchAccount(); } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  if (!account) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="account-detail-page">
      <button onClick={() => navigate('/accounts')} className="flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0052FF] mb-4"><ArrowLeft size={16} /> Back to Accounts</button>

      <div className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Buildings size={22} className="text-[#0D7377]" />
              <h1 className="font-heading text-xl font-bold text-[#0A0A0A]">{account.name}</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono text-[#52525B]">{account.account_id}</span>
              <span className={`${BADGE} ${account.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{account.status === 'active' ? 'Active' : 'Inactive'}</span>
              <span className="text-xs text-[#A1A1AA]">{account.cases_count || 0} case(s)</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={toggleStatus} className={`px-3 py-1.5 text-xs font-medium rounded-md border ${account.status === 'active' ? 'border-[#E4E4E7] hover:bg-[#F0F0F1]' : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'}`}>{account.status === 'active' ? 'Deactivate' : 'Make Active'}</button>
            {!editing && <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-xs font-medium border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] flex items-center gap-1"><PencilSimple size={12} /> Edit</button>}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-[#E4E4E7] grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium mb-1">Address</p>
            {!editing ? (
              <p className="text-sm">{account.address || '—'}</p>
            ) : (
              <textarea value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} rows={2} className="w-full px-2 py-1 text-sm border border-[#E4E4E7] rounded-md" />
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium mb-1">Created</p>
            <p className="text-sm">{account.created_at ? new Date(account.created_at).toLocaleString() : '—'}</p>
            <p className="text-xs text-[#A1A1AA]">by {account.created_by}</p>
          </div>
          {editing && (
            <div>
              <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium mb-1">Name</p>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-2 py-1 text-sm border border-[#E4E4E7] rounded-md" />
            </div>
          )}
        </div>
        {editing && (
          <div className="flex gap-2 mt-4">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Cancel</button>
            <button onClick={save} className="px-3 py-1.5 text-xs bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Save</button>
          </div>
        )}
      </div>

      {/* Linked cases */}
      <h3 className="font-heading text-base font-semibold mb-3">Linked Cases ({account.cases_count || 0})</h3>
      {(account.cases || []).length === 0 ? (
        <div className="bg-white border border-[#E4E4E7] rounded-md p-8 text-center text-sm text-[#52525B]">No cases linked to this account yet.</div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Case</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Type</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Relation</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Risk</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Updated</th>
              </tr>
            </thead>
            <tbody>
              {(account.cases || []).map(c => (
                <tr key={c.id} className="border-b border-[#F0F0F1] hover:bg-[#F7F7F8] cursor-pointer" onClick={() => navigate(`/cases/${c.id}`)}>
                  <td className="px-4 py-2.5 text-sm font-medium text-[#0052FF] hover:underline">{c.full_name}</td>
                  <td className="px-4 py-2.5 text-xs">{formatLabel(c.case_type)}</td>
                  <td className="px-4 py-2.5 text-xs">{displayRelation(c.account_relation) || '—'}</td>
                  <td className="px-4 py-2.5"><span className={STATUS_STYLES[c.status] || BADGE}>{formatLabel(c.status)}</span></td>
                  <td className="px-4 py-2.5"><span className={RISK_STYLES[c.risk_rating] || BADGE}>{c.risk_rating}</span></td>
                  <td className="px-4 py-2.5 text-xs text-[#A1A1AA]">{c.updated_at ? timeAgo(c.updated_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
